// Form for creating / editing a partner service. Variants = duration tiers.
import { useState } from 'react';
import { axiosInstance } from '../../config/axios';
import { toast } from 'sonner';
import PartnerForm from './PartnerForm';
import { ListingToggle } from './_shared';

const CATEGORIES = [
  'Body Massages',
  'Body Scrubs',
  'Body Masques',
  'Face Refreshers',
  'Foot Therapies',
  'Head Massages',
  'Other',
];

// IMPORTANT: Defined outside the parent component so React preserves the
// element identity across renders; otherwise inputs unmount on every keystroke
// and lose focus.
function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold text-gray-500 uppercase tracking-widest mb-1">
        {label}
      </label>
      {children}
    </div>
  );
}

export default function ServiceForm({ initial, partners: partnersProp = [], onSave, onCancel }) {
  const [partners, setPartners] = useState(partnersProp);
  const [partnerModalOpen, setPartnerModalOpen] = useState(false);
  const [form, setForm] = useState(() => ({
    name: initial?.name || '',
    description: initial?.description || '',
    images: initial?.images || [],
    service_category: initial?.service_category || CATEGORIES[0],
    partner_id: initial?.partner_id || (partnersProp[0]?.id ?? ''),
    available_cities: initial?.available_cities || ['Mumbai'],
    combo_only: initial?.combo_only || false,
    partner_url: initial?.partner_url || '',
    is_active: initial?.is_active ?? true,
    variants: initial?.variants?.length
      ? initial.variants
      : [{ duration_minutes: 60, price: '', label: '60 mins' }],
  }));
  const [imageInput, setImageInput] = useState('');
  const [cityInput, setCityInput] = useState('');

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addImage = () => {
    if (!imageInput.trim()) return;
    update('images', [...form.images, imageInput.trim()]);
    setImageInput('');
  };
  const removeImage = (i) =>
    update('images', form.images.filter((_, idx) => idx !== i));

  const addCity = () => {
    if (!cityInput.trim()) return;
    update('available_cities', [...new Set([...form.available_cities, cityInput.trim()])]);
    setCityInput('');
  };
  const removeCity = (c) =>
    update('available_cities', form.available_cities.filter((x) => x !== c));

  const updateVariant = (idx, key, val) => {
    const vs = [...form.variants];
    vs[idx] = { ...vs[idx], [key]: val };
    update('variants', vs);
  };
  const addVariant = () =>
    update('variants', [
      ...form.variants,
      { duration_minutes: 60, price: '', label: '60 mins' },
    ]);
  const removeVariant = (idx) =>
    update('variants', form.variants.filter((_, i) => i !== idx));

  const submit = () => {
    const payload = {
      name: form.name,
      description: form.description,
      images: form.images,
      service_category: form.service_category,
      partner_id: form.partner_id || null,
      available_cities: form.available_cities,
      combo_only: form.combo_only,
      partner_url: form.partner_url || null,
      is_active: form.is_active,
      variants: form.variants.map((v) => ({
        duration_minutes: parseInt(v.duration_minutes, 10),
        price: v.price === '' || v.price == null ? null : parseFloat(v.price),
        label: v.label || `${v.duration_minutes} mins`,
      })),
    };
    onSave(payload);
  };

  return (
    <div className="space-y-4">
      <ListingToggle
        checked={form.is_active}
        onChange={(v) => update('is_active', v)}
        noun="service"
      />

      <Field label="Name">
        <input
          data-testid="service-form-name"
          value={form.name}
          onChange={(e) => update('name', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
        />
      </Field>

      <Field label="Description">
        <textarea
          data-testid="service-form-description"
          rows={3}
          value={form.description}
          onChange={(e) => update('description', e.target.value)}
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
        />
      </Field>

      <div className="grid grid-cols-2 gap-3">
        <Field label="Category">
          <select
            data-testid="service-form-category"
            value={form.service_category}
            onChange={(e) => update('service_category', e.target.value)}
            className="w-full px-3 py-3 border-2 border-gray-200 rounded-xl text-sm"
          >
            {CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Partner">
          <div className="flex gap-2">
            <select
              data-testid="service-form-partner"
              value={form.partner_id}
              onChange={(e) => update('partner_id', e.target.value)}
              className="flex-1 px-3 py-3 border-2 border-gray-200 rounded-xl text-sm"
            >
              <option value="">— None —</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setPartnerModalOpen(true)}
              data-testid="service-form-new-partner"
              className="px-3 py-2 bg-[#2B3A67] text-white font-bold text-xs rounded-xl whitespace-nowrap"
              title="Create new partner"
            >
              <i className="ph-bold ph-plus" /> New
            </button>
          </div>
        </Field>
      </div>

      {partnerModalOpen && (
        <PartnerForm
          asModal
          onCancel={() => setPartnerModalOpen(false)}
          onSave={async (payload) => {
            try {
              const r = await axiosInstance.post('/admin/partners', payload);
              const created = r.data;
              setPartners((prev) => [...prev, created]);
              update('partner_id', created.id);
              setPartnerModalOpen(false);
              toast.success(`Partner "${created.name}" created`);
            } catch (e) {
              toast.error(e.response?.data?.detail || 'Failed to create partner');
            }
          }}
        />
      )}

      <Field label="Image URLs">
        <div className="flex gap-2 mb-2">
          <input
            value={imageInput}
            onChange={(e) => setImageInput(e.target.value)}
            placeholder="https://..."
            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm"
          />
          <button
            type="button"
            onClick={addImage}
            className="px-4 py-2 bg-[#2B3A67] text-white font-bold text-sm rounded-xl"
          >
            Add
          </button>
        </div>
        {form.images.map((img, i) => (
          <div key={i} className="flex items-center gap-2 mb-1">
            <img src={img} alt="" className="w-10 h-10 rounded object-cover" />
            <span className="text-xs text-gray-500 truncate flex-1">{img}</span>
            <button
              type="button"
              onClick={() => removeImage(i)}
              className="text-[#E54562] text-xs font-bold"
            >
              Remove
            </button>
          </div>
        ))}
      </Field>

      <Field label="Available Cities">
        <div className="flex gap-2 mb-2">
          <input
            value={cityInput}
            onChange={(e) => setCityInput(e.target.value)}
            placeholder="e.g. Mumbai"
            className="flex-1 px-3 py-2 border-2 border-gray-200 rounded-xl text-sm"
          />
          <button
            type="button"
            onClick={addCity}
            className="px-4 py-2 bg-[#2B3A67] text-white font-bold text-sm rounded-xl"
          >
            Add
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {form.available_cities.map((c) => (
            <span
              key={c}
              className="text-xs font-bold px-3 py-1 bg-gray-100 rounded-full flex items-center gap-1"
            >
              {c}
              <button onClick={() => removeCity(c)} className="text-[#E54562]">
                ×
              </button>
            </span>
          ))}
        </div>
      </Field>

      <Field label="Variants (Duration tiers)">
        <div className="space-y-2">
          {form.variants.map((v, idx) => (
            <div key={idx} className="bg-gray-50 rounded-xl p-3 space-y-2">
              <div className="grid grid-cols-3 gap-2">
                <input
                  type="number"
                  placeholder="Mins"
                  value={v.duration_minutes}
                  onChange={(e) => updateVariant(idx, 'duration_minutes', e.target.value)}
                  className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm"
                />
                <input
                  type="number"
                  placeholder="Price ₹ (blank=enquiry)"
                  value={v.price ?? ''}
                  onChange={(e) => updateVariant(idx, 'price', e.target.value)}
                  className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm"
                />
                <input
                  placeholder="Label"
                  value={v.label}
                  onChange={(e) => updateVariant(idx, 'label', e.target.value)}
                  className="px-3 py-2 border-2 border-gray-200 rounded-lg text-sm"
                />
              </div>
              {form.variants.length > 1 && (
                <button
                  onClick={() => removeVariant(idx)}
                  type="button"
                  className="text-xs font-bold text-[#E54562]"
                >
                  Remove variant
                </button>
              )}
            </div>
          ))}
          <button
            type="button"
            onClick={addVariant}
            className="text-xs font-bold text-[#2B3A67]"
          >
            + Add variant
          </button>
        </div>
      </Field>

      <Field label="Partner URL (optional)">
        <input
          value={form.partner_url}
          onChange={(e) => update('partner_url', e.target.value)}
          placeholder="https://partnersite.com/services"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-sm"
        />
      </Field>

      <label className="flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={form.combo_only}
          onChange={(e) => update('combo_only', e.target.checked)}
        />
        <span>Combo only — no standalone price; users submit an enquiry.</span>
      </label>

      <div className="flex gap-2 pt-3">
        <button
          onClick={onCancel}
          className="flex-1 py-3 border-2 border-gray-300 text-gray-500 font-bold rounded-2xl"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          data-testid="service-form-submit"
          className="flex-1 py-3 bg-[#E54562] text-white font-extrabold rounded-2xl"
        >
          Save
        </button>
      </div>
    </div>
  );
}
